import { useEffect, useMemo, useRef, useState } from 'react';
import { adminAPI, fmtError } from '@/lib/api';
import { formatMoney, formatInt, prettyPhase, prettyPlan } from '@/lib/format';
import { Eye, EyeOff, Copy, Upload, X } from 'lucide-react';

export default function AdminAccounts() {
  const [accs, setAccs] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ reason: '', mt_login: '', mt_password: '', mt_server: 'Deriv-Demo', pnl_usd: '', note: '' });
  const [proofImage, setProofImage] = useState(''); // base64 data-URL for the breach proof screenshot
  const [proofError, setProofError] = useState('');
  const proofInputRef = useRef(null);
  const [err, setErr] = useState('');
  const [showBreached, setShowBreached] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [revealed, setRevealed] = useState({}); // per-row password reveal

  const load = () => adminAPI.accounts().then(setAccs).catch(e => setErr(fmtError(e)));
  useEffect(() => { load(); }, []);

  const { visible, breachedCount } = useMemo(() => {
    const b = accs.filter(a => a.status === 'breached');
    const v = showBreached ? accs : accs.filter(a => a.status !== 'breached');
    return { visible: v, breachedCount: b.length };
  }, [accs, showBreached]);

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text || '');
      // small visual pulse — set revealed briefly
      setRevealed(r => ({ ...r, [`${key}_copied`]: true }));
      setTimeout(() => setRevealed(r => { const c = { ...r }; delete c[`${key}_copied`]; return c; }), 1000);
    } catch {}
  };

  const submitBreach = async () => {
    setErr('');
    try {
      await adminAPI.breach(modal.acc.account_id, {
        reason: form.reason,
        proof_image: proofImage || undefined,
      });
      setModal(null); setProofImage(''); setProofError(''); load();
    } catch (e) { setErr(fmtError(e)); }
  };

  // Convert an uploaded File → base64 data URL (max 6 MB; images only).
  const onProofPicked = (file) => {
    setProofError('');
    if (!file) return;
    if (!file.type || !file.type.startsWith('image/')) {
      setProofError('Please upload an image file (PNG, JPG, WEBP).');
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      setProofError('File is over 6 MB. Please compress and try again.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setProofImage(String(reader.result || ''));
    reader.onerror = () => setProofError('Could not read file.');
    reader.readAsDataURL(file);
  };
  const submitCreds = async () => {
    setErr('');
    try {
      await adminAPI.assignCreds(modal.acc.account_id, { mt_login: form.mt_login, mt_password: form.mt_password, mt_server: form.mt_server });
      setModal(null); load();
    } catch (e) { setErr(fmtError(e)); }
  };
  const submitPnl = async () => {
    setErr('');
    try {
      const payload = form.pnl_usd === '' || form.pnl_usd === null
        ? { pnl_usd: null }
        : { pnl_usd: Number(form.pnl_usd), note: form.note || '' };
      await adminAPI.setPnL(modal.acc.account_id, payload);
      setModal(null); load();
    } catch (e) { setErr(fmtError(e)); }
  };
  const submitReassign = async () => {
    setErr('');
    try {
      await adminAPI.unpauseAccount(modal.acc.account_id, {
        new_product_type: form.new_product_type || 'one_step',
        new_account_size: Number(form.new_account_size || 5000),
        note: form.note || '',
      });
      setModal(null); load();
    } catch (e) { setErr(fmtError(e)); }
  };
  // Plain unpause — no reassignment. Separate from submitReassign so
  // reassigning a paused account to a new product/size stays an explicit,
  // optional admin choice rather than something bundled into every unpause.
  const submitSimpleUnpause = async (accountId) => {
    setErr('');
    try {
      await adminAPI.unpauseAccount(accountId);
      load();
    } catch (e) { setErr(fmtError(e)); }
  };

  return (
    <div className="space-y-5">
      {err && <div className="text-sm text-[#ff3366]">{err}</div>}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl">Trading accounts</h1>
          <p className="text-xs text-zinc-500 mt-1">
            Showing <span className="text-white font-mono">{visible.length}</span> active accounts
            {breachedCount > 0 && !showBreached && (
              <> · <span className="text-[#ff3366]">{breachedCount} breached hidden</span></>
            )}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-zinc-400 select-none cursor-pointer" data-testid="toggle-passwords-wrap">
            <input
              type="checkbox"
              className="accent-[#00ff66]"
              checked={showPasswords}
              onChange={e => setShowPasswords(e.target.checked)}
              data-testid="toggle-passwords"
            />
            <span className="inline-flex items-center gap-1">
              {showPasswords ? <Eye size={13} className="text-[#00ff66]" /> : <EyeOff size={13} />}
              Show passwords
            </span>
          </label>
          <label className="flex items-center gap-2 text-sm text-zinc-400 select-none cursor-pointer" data-testid="toggle-breached-wrap">
            <input
              type="checkbox"
              className="accent-[#00ff66]"
              checked={showBreached}
              onChange={e => setShowBreached(e.target.checked)}
              data-testid="toggle-breached"
            />
            Show breached ({breachedCount})
          </label>
        </div>
      </div>
      <div className="card-tech rounded-lg overflow-x-auto">
        <div className="min-w-[1280px]">
          <div className="grid grid-cols-[1.4fr_0.9fr_1.1fr_0.9fr_0.9fr_0.9fr_1fr_0.9fr_1.5fr] gap-3 px-4 py-3 label-xs border-b border-white/5">
            <div>TRADER</div><div>#LOGIN</div><div>PASSWORD</div><div>PLAN</div><div>SIZE</div><div>PHASE</div><div>EQUITY</div><div>PNL</div><div>ACTIONS</div>
          </div>
          {visible.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-zinc-500" data-testid="admin-accounts-empty">
              No accounts to display{!showBreached && breachedCount > 0 ? ' — toggle "Show breached" above to view them.' : '.'}
            </div>
          )}
          {visible.map(a => {
            const isManual = a.manual_pnl_usd !== null && a.manual_pnl_usd !== undefined;
            const pnl = isManual ? a.manual_pnl_usd : (a.metrics?.pnl ?? 0);
            const rowRevealed = showPasswords || !!revealed[a.account_id];
            const pw = a.mt_password || '';
            const pwMasked = pw ? '•'.repeat(Math.min(pw.length, 10)) : '—';
            const justCopied = revealed[`${a.account_id}_copied`];
            return (
              <div key={a.account_id} className="grid grid-cols-[1.4fr_0.9fr_1.1fr_0.9fr_0.9fr_0.9fr_1fr_0.9fr_1.5fr] gap-3 px-4 py-3 border-b border-white/5 text-sm items-center" data-testid={`admin-acc-${a.account_id}`}>
                <div className="text-zinc-300 truncate">
                  <div className="text-white text-sm" title={a.user_email}>{a.user_email || a.user_id}</div>
                  {a.user_name && <div className="text-xs text-zinc-500 truncate">{a.user_name}</div>}
                </div>
                <div className="font-mono">{a.mt_login || '—'}</div>
                <div className="flex items-center gap-1.5 min-w-0" data-testid={`pw-cell-${a.account_id}`}>
                  <span className={`font-mono text-xs truncate ${rowRevealed ? 'text-white' : 'text-zinc-500'}`} title={rowRevealed ? pw : 'Hidden'}>
                    {rowRevealed ? (pw || '—') : pwMasked}
                  </span>
                  {pw && (
                    <>
                      <button
                        onClick={() => setRevealed(r => ({ ...r, [a.account_id]: !r[a.account_id] }))}
                        className="text-zinc-500 hover:text-white transition"
                        title={rowRevealed ? 'Hide' : 'Reveal'}
                        data-testid={`pw-toggle-${a.account_id}`}
                      >
                        {rowRevealed ? <EyeOff size={11} /> : <Eye size={11} />}
                      </button>
                      <button
                        onClick={() => copyToClipboard(pw, a.account_id)}
                        className={`transition ${justCopied ? 'text-[#00ff66]' : 'text-zinc-500 hover:text-white'}`}
                        title={justCopied ? 'Copied' : 'Copy password'}
                        data-testid={`pw-copy-${a.account_id}`}
                      >
                        <Copy size={11} />
                      </button>
                    </>
                  )}
                </div>
                <div>{prettyPlan(a.plan_id)}</div>
                <div className="font-mono">${formatInt(a.account_size)}</div>
                <div><span className={`chip ${a.status === 'breached' ? 'chip-red' : a.phase === 'funded' ? 'chip-green' : 'chip-yellow'}`}>{prettyPhase(a.phase)}</span></div>
                <div className="font-mono">${formatMoney(a.metrics?.equity)}</div>
                <div className={`font-mono ${pnl >= 0 ? 'text-[#00ff66]' : 'text-[#ff3366]'}`} title={isManual ? 'Admin-set' : 'Simulated'}>
                  {pnl >= 0 ? '+' : ''}{formatMoney(pnl)}
                  {isManual && <span className="ml-1 text-[10px] text-[#00ff66]/70">M</span>}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button className="btn-ghost btn-sm" onClick={() => { setForm({ ...form, pnl_usd: isManual ? a.manual_pnl_usd : '', note: a.manual_pnl_note || '' }); setModal({ type: 'pnl', acc: a }); setErr(''); }} data-testid={`pnlbtn-${a.account_id}`}>Set PnL</button>
                  <button className="btn-ghost btn-sm" onClick={() => { setForm({ ...form, mt_login: a.mt_login, mt_password: a.mt_password, mt_server: a.mt_server }); setModal({ type: 'creds', acc: a }); setErr(''); }} data-testid={`credbtn-${a.account_id}`}>Set creds</button>
                  {a.is_paused && (
                    <>
                      <button className="btn-ghost btn-sm text-[#00ff66]"
                              onClick={() => submitSimpleUnpause(a.account_id)}
                              data-testid={`unpausebtn-${a.account_id}`}>
                        Unpause
                      </button>
                      <button className="btn-ghost btn-sm text-[#00ff66]"
                              onClick={() => { setForm({ ...form, new_product_type: 'one_step', new_account_size: '5000', note: '' }); setModal({ type: 'reassign', acc: a }); setErr(''); }}
                              data-testid={`reassignbtn-${a.account_id}`}>
                        Unpause & Reassign
                      </button>
                    </>
                  )}
                  {a.status !== 'breached' && <button className="btn-ghost btn-sm text-[#ff3366]" onClick={() => { setForm({ ...form, reason: '' }); setProofImage(''); setProofError(''); setModal({ type: 'breach', acc: a }); setErr(''); }} data-testid={`breachbtn-${a.account_id}`}>Breach</button>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="card-tech rounded-lg p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-2xl">
              {modal.type === 'breach' ? 'Mark account breached'
               : modal.type === 'creds' ? 'Assign credentials'
               : modal.type === 'reassign' ? 'Unpause & reassign'
               : 'Update PnL'}
            </h3>
            <p className="text-xs text-zinc-500 mt-1">Account #{modal.acc.mt_login} · {modal.acc.user_email}</p>

            {modal.type === 'breach' && (
              <>
                <textarea
                  className="input-tech mt-4"
                  rows={3}
                  placeholder="Reason (e.g., Daily DD hit)"
                  value={form.reason}
                  onChange={e => setForm({ ...form, reason: e.target.value })}
                  data-testid="breach-reason-input"
                />

                {/* Evidence screenshot */}
                <label className="label-xs mt-4 block">EVIDENCE SCREENSHOT (OPTIONAL)</label>
                {proofImage ? (
                  <div className="relative mt-2 rounded-md overflow-hidden border border-white/10 bg-black/40" data-testid="breach-proof-preview-wrap">
                    <img src={proofImage} alt="Breach evidence" className="block w-full max-h-64 object-contain bg-black" data-testid="breach-proof-preview" />
                    <button
                      onClick={() => { setProofImage(''); setProofError(''); if (proofInputRef.current) proofInputRef.current.value = ''; }}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 border border-white/10 text-zinc-300 hover:text-white flex items-center justify-center"
                      data-testid="breach-proof-remove-btn"
                      aria-label="Remove screenshot"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="breach-proof-file"
                    className="mt-2 flex items-center justify-center gap-2 rounded-md border border-dashed border-white/15 bg-white/[0.02] hover:bg-white/[0.04] px-4 py-6 cursor-pointer transition"
                    data-testid="breach-proof-dropzone"
                  >
                    <Upload size={16} className="text-zinc-400" />
                    <div className="text-sm">
                      <span className="text-white">Attach a screenshot</span>
                      <span className="text-zinc-500"> · PNG/JPG · max 6 MB</span>
                    </div>
                  </label>
                )}
                <input
                  id="breach-proof-file"
                  ref={proofInputRef}
                  type="file"
                  accept="image/*"
                  onChange={e => onProofPicked(e.target.files?.[0])}
                  className="hidden"
                  data-testid="breach-proof-file-input"
                />
                {proofError && (
                  <div className="text-[11px] text-[#ff3366] mt-2" data-testid="breach-proof-error">{proofError}</div>
                )}

                <div className="flex justify-end gap-2 mt-4">
                  <button className="btn-ghost btn-sm" onClick={() => { setModal(null); setProofImage(''); setProofError(''); }}>Cancel</button>
                  <button className="btn-primary btn-sm" onClick={submitBreach} data-testid="submit-breach">Confirm breach</button>
                </div>
              </>
            )}
            {modal.type === 'creds' && (
              <>
                <input className="input-tech mt-4" placeholder="MT5 Login" value={form.mt_login} onChange={e => setForm({ ...form, mt_login: e.target.value })} />
                <input className="input-tech" placeholder="MT5 Password" value={form.mt_password} onChange={e => setForm({ ...form, mt_password: e.target.value })} />
                <input className="input-tech" placeholder="MT5 Server (e.g., Deriv-Demo)" value={form.mt_server} onChange={e => setForm({ ...form, mt_server: e.target.value })} />
                <div className="flex justify-end gap-2 mt-4">
                  <button className="btn-ghost btn-sm" onClick={() => setModal(null)}>Cancel</button>
                  <button className="btn-primary btn-sm" onClick={submitCreds}>Save</button>
                </div>
              </>
            )}
            {modal.type === 'reassign' && (
              <>
                <p className="text-sm text-zinc-400 mt-4">Reassign this paused legacy account to a new product. We'll atomically pull an AVAILABLE row from the pool.</p>
                <label className="label-xs mt-4 block">NEW PRODUCT TYPE</label>
                <select className="input-tech" value={form.new_product_type || 'one_step'} onChange={e => setForm({ ...form, new_product_type: e.target.value })} data-testid="reassign-product-type">
                  <option value="one_step">One-Step Program</option>
                  <option value="instant">Instant Funding</option>
                  <option value="funded">Funded (skip evaluation)</option>
                </select>
                <label className="label-xs mt-4 block">NEW ACCOUNT SIZE</label>
                <select className="input-tech" value={form.new_account_size || '5000'} onChange={e => setForm({ ...form, new_account_size: e.target.value })} data-testid="reassign-account-size">
                  <option value="5000">$5,000</option>
                  <option value="10000">$10,000</option>
                  <option value="20000">$20,000</option>
                </select>
                <textarea className="input-tech mt-3" rows={2} placeholder="Note (optional)" value={form.note || ''} onChange={e => setForm({ ...form, note: e.target.value })} />
                <div className="flex justify-end gap-2 mt-4">
                  <button className="btn-ghost btn-sm" onClick={() => setModal(null)}>Cancel</button>
                  <button className="btn-primary btn-sm" onClick={submitReassign} data-testid="submit-reassign">Reassign</button>
                </div>
              </>
            )}
            {modal.type === 'pnl' && (
              <>
                <p className="text-sm text-zinc-400 mt-4">Override the simulated PnL for this account. Leave blank to revert to auto-simulation.</p>
                <input type="number" step="0.01" className="input-tech mt-4" placeholder="PnL in USD (e.g., 250 or -75)" value={form.pnl_usd} onChange={e => setForm({ ...form, pnl_usd: e.target.value })} />
                <textarea className="input-tech" rows={2} placeholder="Note (optional)" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
                <div className="flex justify-end gap-2 mt-4">
                  <button className="btn-ghost btn-sm" onClick={() => setModal(null)}>Cancel</button>
                  <button className="btn-primary btn-sm" onClick={submitPnl}>Save</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}